/**
 * Party Resume Options - All the multi-select and prompt options
 */

// Gathering Types - grouped by category
export const GATHERING_TYPES = {
  intimate: {
    label: 'Intimate & Conversational',
    options: [
      'Private dinner parties',
      'Salon-style conversations',
      'Wine/cocktail tasting gatherings',
      'Small house parties (10-25 people)',
      'Cuddle / connection spaces',
      'Tea ceremonies / slow social spaces',
      'Fireside or storytelling nights',
    ],
  },
  creative: {
    label: 'Creative & Cultural',
    options: [
      'Comedy',
      'Art openings / gallery nights',
      'Live music or performance salons',
      'Poetry / reading gatherings',
      'Film screenings',
      'Fashion/styling events',
      'Photography or content-creation gatherings',
      'Maker/creator meetups',
    ],
  },
  dance: {
    label: 'Dance & Nightlife',
    options: [
      'DJ-driven dance parties',
      'Underground/warehouse parties',
      'Club nights with curated guest lists',
      'House music / techno gatherings',
      'Burning Man-style or festival-inspired events',
      'Costume or themed dance parties',
      'After-hours / sunrise sets',
    ],
  },
  wellness: {
    label: 'Wellness & Embodied Experiences',
    options: [
      'Breathwork or meditation gatherings',
      'Yoga / movement-based socials',
      'Sauna / cold plunge / bathhouse socials',
      'Conscious or sober dance events',
      'Psychedelic integration circles',
      'Ritual or ceremonial gatherings',
      'Touch-positive or connection workshops',
    ],
  },
  hedonistic: {
    label: 'Hedonistic / Sensual',
    options: [
      'Play parties',
      'Kink/BDSM-friendly spaces',
      'Sensual but non-sexual social spaces',
      'Boudoir or erotic arts gatherings',
      'Intimacy-focused workshops',
      'Clothing-optional environments',
      'Power-dynamic or D/s social spaces',
    ],
  },
  intellectual: {
    label: 'Intellectual & Professional',
    options: [
      'Networking with interesting people',
      'Founder/creative industry salons',
      'Idea incubators / brainstorming nights',
      'Skill-share gatherings',
      'Panel discussions or speaker nights',
      'Cultural commentary or debate nights',
    ],
  },
  adventure: {
    label: 'Adventure & Activity-based',
    options: [
      'Outdoor gatherings (beach, park, woods)',
      'Day parties / pool parties',
      'Dinner + activity (bowling, games, etc.)',
      'Travel-based group experiences',
      'Retreat-style weekends',
      'Festival group camps',
    ],
  },
  hostSupport: {
    label: 'Host-support & Community Roles',
    options: [
      'I enjoy helping hosts run the space',
      'I like co-creating atmosphere',
      "I'm open to performance or participation roles",
      'I prefer to attend and observe',
      'Depends on event',
    ],
  },
};

// Behavioral Prompts - the 13+ optional prompts
export const BEHAVIORAL_PROMPTS = [
  { key: 'howIShowUp', label: 'How I show up', placeholder: 'Describe your energy when you arrive...' },
  { key: 'whatImHereFor', label: "What I'm here for", placeholder: 'What are you seeking in gatherings...' },
  { key: 'whenIWalkIn', label: 'When I walk into a room, I usually...', placeholder: 'Your typical entrance style...' },
  { key: 'notAFitFor', label: 'Not a fit for...', placeholder: 'Types of events or vibes that don\'t work for you...' },
  { key: 'atMyBest', label: "I'm at my best in gatherings that feel...", placeholder: 'Describe your ideal atmosphere...' },
  { key: 'peoplePullMeAside', label: 'People tend to pull me aside to talk about...', placeholder: 'Topics you\'re known for...' },
  { key: 'neverEmptyHanded', label: 'I never come empty-handed — I usually bring...', placeholder: 'Your signature contribution...' },
  { key: 'naturalRole', label: 'The role I naturally fall into is...', placeholder: 'Host helper, connector, dancer, listener...' },
  { key: 'dontThriveAround', label: "The kind of energy I don't thrive around is...", placeholder: 'What drains you...' },
  { key: 'leaveFeeling', label: 'If the night goes right, I leave feeling...', placeholder: 'Your ideal outcome...' },
  { key: 'comfortableWhen', label: "I'm most comfortable when the room is...", placeholder: 'Size, vibe, energy level...' },
  { key: 'greatGathering', label: 'A great gathering, to me, always includes...', placeholder: 'Must-haves for a good night...' },
  { key: 'surprisedThat', label: 'People are usually surprised that I...', placeholder: 'Something unexpected about you...' },
];

// Contribution tags - what you bring beyond presence
export const CONTRIBUTION_TAGS = [
  'DJ skills',
  'Emotional intelligence',
  'Humor',
  'Photography',
  'Aesthetic styling',
  'Hosting support',
  'Ritual facilitation',
  'Cleanup angel',
  'Great conversationalist',
  'Dance floor starter',
  'Connector',
  'Storyteller',
  'Cook/food prep',
  'Mixology',
  'Music curation',
  'Performance art',
  'Grounding presence',
  'Mischief maker',
];

// "Are you more likely to..." options
export const LIKELY_TO_OPTIONS = [
  'Start a dance floor',
  'Spark a deep conversation',
  'Introduce interesting people',
  'Create mischief',
  'Hold grounding energy',
  'Document the moment',
];

// Substances relationship options
export const SUBSTANCES_RELATIONSHIP = [
  { value: 'sober', label: 'Sober' },
  { value: 'light_social', label: 'Light/social' },
  { value: 'situational', label: 'Situational' },
  { value: 'prefer_substance_free', label: 'Prefer substance-free environments' },
];

// Arrival time preferences
export const ARRIVAL_TIMES = [
  { value: 'early', label: 'Early — help set the tone' },
  { value: 'peak', label: 'Peak hour' },
  { value: 'late', label: 'Late-night closer' },
  { value: 'sunrise', label: 'Sunrise crew' },
];

// After-party style
export const AFTER_PARTY_STYLES = [
  { value: 'vanish', label: 'Vanish mysteriously' },
  { value: 'diner', label: 'Diner crew' },
  { value: 'kitchen', label: 'Deep talk kitchen hang' },
  { value: 'sunrise_walk', label: 'Sunrise walk' },
  { value: 'depends', label: 'Depends on the night' },
];

// RSVP style
export const RSVP_STYLES = [
  { value: 'early_reliable', label: 'RSVPs early and reliably' },
  { value: 'spontaneous', label: 'Tends to be spontaneous' },
  { value: 'depends', label: 'Depends on mood/day' },
];
